/** Regime and premium strip under the DTE timeline. Descriptive only. */
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { EarningsQualityBadge, WorkspacePanel } from "@/workspace/primitives";
import { earningsConflict, regimeCompatibility } from "@/workspace/mockTickerData";
import { REGIME_CONTEXT } from "@/workspace/screenLanguage";
import { HISTORICAL_CONTEXT_KIND } from "@/workspace/live/workspaceLiveTypes";

const CONTEXT_TIER = {
  available: "info",
  limited: "info",
  differs: "caution",
};

const CONTEXT_COPY = {
  available: REGIME_CONTEXT.available,
  limited: REGIME_CONTEXT.limited,
  differs: REGIME_CONTEXT.differs,
};

export function RegimePremiumStrip({ ticker, dte, familyId = "income" }) {
  const conflict = earningsConflict(ticker.earnings, dte);
  const familyLabel = familyId === "protection" ? "Protection / hedging" : "Income";
  const compat = ticker.live
    ? null
    : regimeCompatibility(ticker.regime.confirmedState === "transitioning" ? ticker.regime.instantState : ticker.regime.confirmedState);
  const row = compat ? (compat.find((c) => c.family === familyLabel) || compat[0]) : null;
  const contextKey = ticker.live
    ? (ticker.historicalContext?.kind === HISTORICAL_CONTEXT_KIND.historical_limited
      || ticker.historicalContext?.kind === HISTORICAL_CONTEXT_KIND.historical_unavailable
      ? "limited"
      : "available")
    : (row?.verdict || "limited");
  const premium = ticker.premium || { label: "IV context unavailable", ivContext: "typical", expectedMoveNote: "" };
  const incomeNote = ticker.live
    ? (ticker.currentQuote?.availability === "unverified_timestamp"
      ? "Option quote timestamp is unavailable. This strip is historical and cached context, not a current option quote."
      : "Historical and cached premium context only. Not a current option quote.")
    : conflict.state === "inside" && conflict.quality === "confirmed"
      ? "Confirmed earnings event falls inside selected time window. Quote fields and policy parameters still describe product action."
      : conflict.state === "predicted"
        ? "Earnings date is predicted, not confirmed."
        : ticker.optionsState === "unverified"
          ? "Option quote timestamp is unavailable."
          : "Quote fields and configured parameters describe the product-action layer.";

  return (
    <WorkspacePanel
      title="Regime and premium environment"
      subtitle="Historical and premium context only"
      testid="regime-premium-strip"
    >
      <div className="space-y-1.5 text-[11px]" style={{ color: "var(--hoip-text-secondary)" }}>
        <div>
          <span className="font-mono-data">{ticker.regime.confirmedState}</span>
          {" · "}
          {Math.round(ticker.regime.confidence * 100)}% confidence
          <span className="ml-1.5">
            <StatusBadge
              tier={CONTEXT_TIER[contextKey] || "info"}
              text={CONTEXT_COPY[contextKey] || REGIME_CONTEXT.limited}
            />
          </span>
        </div>
        <div>{premium.label}</div>
        {premium.expectedMoveNote ? (
          <div style={{ color: "var(--hoip-text-tertiary)" }}>{premium.expectedMoveNote}</div>
        ) : null}
        <div className="flex flex-wrap items-center gap-1.5">
          <span>
            {ticker.live
              ? `${conflict.label}${dte != null ? ` · requested ${dte}-DTE analysis tenor` : ""}`
              : conflict.state === "inside" && conflict.quality === "confirmed"
                ? `Confirmed earnings event falls inside selected ${dte}-DTE time window`
                : `${conflict.label}${dte ? ` · selected ${dte}-DTE` : ""}`}
          </span>
          {conflict.quality ? <EarningsQualityBadge quality={conflict.quality} /> : null}
        </div>
        <p className="leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>{incomeNote}</p>
      </div>
    </WorkspacePanel>
  );
}
